import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { Prompt } from 'react-router';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useGlobalStyles } from 'GlobalStyles';
import { useShowAlert } from 'hooks';
import useDebounce from 'hooks/debounce';
import useUnload from 'hooks/unload';
import { useCheckEmail } from 'hooks/useCheckEmail';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import { DraftsProviderContext, InitialDraft, milisecondsToSave } from 'providers/DraftsProvider';
import queryString from 'query-string';
import { EntityRoutes } from 'routes/constants';
import API from 'services/API';
import { userHasRole, userHasRoles } from 'services/Authorization';
import { ContactIncludes, HiringAuthorityIncludes } from 'services/includes';
import strings from 'strings';
import { FPActionButton, When } from 'UI/components/atoms';
import { FPCard, FPCardContent } from 'UI/components/atoms/FPCard';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { DuplicateCandidateAlert } from 'UI/components/molecules/DuplicateCandidateOverview';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import CandidateForm from 'UI/components/organisms/CandidateForm';
import { FormFieldsMap, FormFieldsMapForHA } from 'UI/components/organisms/CandidateForm/fields';
import FileManager from 'UI/components/organisms/FileManager';
import {
  formatPhoneNumbers,
  PHONE_FORMAT_MODES
} from 'UI/components/organisms/PhoneNumbersForm/utils';
import {
  DefaultNewCandidateFileCategories,
  DELAY_BOUNCE_TIME,
  drawerAnchor,
  waitingTimeBeforeRedirect
} from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import {
  CandidateEntity,
  ContactEntity,
  ContactRole,
  EntityType,
  HiringAuthorityEntity
} from 'UI/constants/entityTypes';
import { Roles } from 'UI/constants/roles';
import { SeverityBySimilarity } from 'UI/constants/status';
import { nestTernary, REQUIRED_VALIDATION } from 'UI/utils';
import { decryptId } from 'UI/utils/encrypt';
import { preloadFromBackend, prepareFormToSubmit, selectValidation } from 'UI/utils/forms';
import { OptionRenderers, Selectors } from 'UI/utils/renderers';

import { CandidateDataSheetDrawer } from './CandidateDataSheetDrawer';
import { CANDIDATE_SOURCES } from './NewCandidate.constants';
import { useStyles } from './NewCandidate.styled';
import { useFeatureFlags } from './NewCandidateContent.hooks';

/**
 * @typedef {NewCandidateProps & Object} Props
 * @property {boolean} isFromProfile
 * @property {string} [hiringId]
 * @property {string} [contactId]
 * @property {object} initialApplicantData
 * @property {Function} onResetForm
 */

const DELETE_API_VERSION = 1;
const { globalSearchPerson } = OptionRenderers;

/**
 * @param {Props} props
 */
const NewCandidateContent = ({
  contactId,
  hiringId,
  history,
  initialApplicantData = null,
  initiatingFromDirectory,
  isFromProfile,
  location,
  onResetForm,
  externalSource
}) => {
  const { canIncludeContactDataSheet, canSaveAsDraft } = useFeatureFlags();
  const {
    deleteDraftById,
    draft,
    hasRequiredFieldsForDraft,
    saveDraft,
    saveOnCancel,
    setDraft,
    updateDraft
  } = useContext(DraftsProviderContext);

  const isFromTalentPool =
    externalSource === CANDIDATE_SOURCES.talentPool || draft?.formData?.applicantId;
  const isFromResumeParser = externalSource === CANDIDATE_SOURCES.resumeParser;

  const { showAlert } = useShowAlert();
  const globalClasses = useGlobalStyles();
  const classes = useStyles();
  const isUserCoach = userHasRole(Roles.Coach);
  const isOpsOrDc = userHasRoles([Roles.Operations, Roles.DataCoordinator]);
  const [isFromDirectory, setIsFromDirectory] = useState(isFromProfile || initiatingFromDirectory);
  const [attachments, setAttachments] = useState([]);
  const [isBluesheetOpen, setIsBluesheetOpen] = useState(false);

  const [uiState, setUiState] = useState({
    hasLoaded: !isFromProfile,
    isLoading: false,
    isReseting: false,
    isSaving: false,
    isSuccess: false
  });

  const preselectedCompany = useMemo(() => {
    try {
      const { company: encryptedId, cName } = queryString.parse(location.search);
      const companyId = encryptedId && decryptId(encryptedId, true)[0];
      return companyId
        ? {
            id: companyId,
            name: cName
          }
        : null;
    } catch (error) {
      showAlert({
        severity: 'error',
        title: 'Invalid Company Reference.',
        error
      });

      return null;
    }
  }, [location.search, showAlert]);

  const buildProfileData = useMemo(() => {
    if (!hiringId && !contactId) return null;

    return hiringId
      ? { id: hiringId, type: HiringAuthorityEntity.id, role_id: ContactRole.HiringAuthority }
      : { id: contactId, type: ContactEntity.id, role_id: ContactRole.Name };
  }, [contactId, hiringId]);

  const defaultValues = draft?.id
    ? {
        ...draft.formData,
        phones: formatPhoneNumbers(draft.formData, PHONE_FORMAT_MODES.preload)
      }
    : nestTernary(!!initialApplicantData, initialApplicantData, {
        [FormFieldsMap.FromContact.key]: buildProfileData,
        [FormFieldsMap.Company.key]: preselectedCompany,
        [FormFieldsMap.Email.key]: ''
      });

  const form = useForm({
    defaultValues
  });

  const { register, reset, setValue, handleSubmit, watch, getValues, unregister } = form;
  const { email, personalEmail } = watch([
    FormFieldsMap.Email.key,
    FormFieldsMap.PersonalEmail.key
  ]);
  const debouncedEmail = useDebounce(email ?? '', DELAY_BOUNCE_TIME).trim();
  const debouncedOtherEmail = useDebounce(personalEmail ?? '', DELAY_BOUNCE_TIME).trim();

  const selectedRecruiter = watch(FormFieldsMap.Recruiter.key);
  const selectedContactOrHA = watch(FormFieldsMap.FromContact.key);
  const dataSheet = watch(FormFieldsMap.DataSheet.key);
  const dataSheetId = dataSheet?.id;

  const [bluesheet, setBluesheet] = useState(draft?.id ? draft.sheetData : null);
  const candidateFiles = initialApplicantData?.attachments
    ? initialApplicantData?.attachments
    : nestTernary(draft?.id, draft.attachments, attachments);

  const resumeHiringId = initialApplicantData?.hiringId;
  const resumeContactId = initialApplicantData?.contactId;

  const shouldCheckContacts =
    !isFromDirectory && !draft?.isFromDirectory && !resumeHiringId && !resumeContactId;

  const { validation: emailVerification, initialData } = useCheckEmail({
    debouncedEmail,
    shouldCheckContacts,
    hasEmail: !!email,
    isFromTalentPool
  });

  const { validation: otherEmailVerification } = useCheckEmail({
    debouncedEmail: debouncedOtherEmail,
    shouldCheckContacts,
    hasEmail: !!personalEmail,
    isFromTalentPool
  });

  const emailValidation = selectValidation(
    { ...emailVerification, email: debouncedEmail },
    { ...otherEmailVerification, email: debouncedOtherEmail },
    initialData
  );

  const handleClickCreateFromContact = async contactClicked => {
    setIsFromDirectory(true);
    setDraft({ ...InitialDraft, type: EntityType.Candidate, isFromDirectory: true });
    setValue(FormFieldsMap.FromContact.key, contactClicked);
    await fetchSelectedContact(contactClicked);
  };
  const canBeSavedAsDraft =
    hasRequiredFieldsForDraft && !emailValidation.isRegistered && !emailValidation.isValidating;

  const shouldDisableSaveButton =
    uiState.isLoading || uiState.isSaving || uiState.isSuccess || emailValidation.isValidating;
  const isSaveDisabled = isOpsOrDc
    ? shouldDisableSaveButton
    : emailValidation.isRegistered || shouldDisableSaveButton;

  useEffect(() => {
    isUserCoach && register({ name: FormFieldsMap.Recruiter.key });
  }, [register, isUserCoach]);

  useEffect(() => {
    register({ name: FormFieldsMap.FromContact.key });
    register({ name: FormFieldsMap.DataSheet.key });
    register(FormFieldsMap.BluesheetCompleted.key, {
      validate: value => {
        return value === true || value === 'true' || REQUIRED_VALIDATION.required;
      }
    });

    return () => {
      unregister(FormFieldsMap.FromContact.key);
      unregister(FormFieldsMap.DataSheet.key);
    };
  }, [register, unregister]);

  const handleUpdateDraft = useCallback(
    () => updateDraft({ ...draft, formData: getValues() }),
    [draft, getValues, updateDraft]
  );

  const handleValuesChanged = useCallback(
    values => setDraft(prev => ({ ...prev, formData: values })),
    [setDraft]
  );

  const saveOrUpdate = useCallback(() => {
    if (canBeSavedAsDraft && !uiState.isSuccess) {
      const currentFormValues = getValues();
      draft.id
        ? handleUpdateDraft(draft)
        : saveDraft({ shouldConfirm: false, values: currentFormValues });
    }
  }, [canBeSavedAsDraft, uiState.isSuccess, getValues, draft, handleUpdateDraft, saveDraft]);

  useUnload(e => {
    e.preventDefault();
    saveOrUpdate();
  });

  useEffect(() => {
    const interval = setInterval(() => {
      saveOrUpdate();
    }, milisecondsToSave);
    return () => {
      clearInterval(interval);
    };
  }, [saveOrUpdate]);

  const preloadContactInfo = useCallback(
    (nameInfo, formFieldsMap) => {
      const preloadedValues = preloadFromBackend(nameInfo, formFieldsMap);
      reset(preloadedValues);

      setValue(FormFieldsMap.Specialty.key, preloadedValues[FormFieldsMap.Specialty.key]);
      setValue(FormFieldsMap.Subspecialty.key, preloadedValues[FormFieldsMap.Subspecialty.key]);
      setValue(
        FormFieldsMap.FunctionalTitle.key,
        preloadedValues[FormFieldsMap.FunctionalTitle.key]
      );
      setValue(FormFieldsMap.State.key, preloadedValues[FormFieldsMap.State.key]);
      setValue(FormFieldsMap.City.key, preloadedValues[FormFieldsMap.City.key]);
      setValue(FormFieldsMap.Source.key, preloadedValues[FormFieldsMap.Source.key]);
      setValue(FormFieldsMap.Phone.key, preloadedValues[FormFieldsMap.Phone.key]);
      setValue(FormFieldsMap.Mobile.key, preloadedValues[FormFieldsMap.Mobile.key]);
      setValue(FormFieldsMap.FromContact.key, preloadedValues[FormFieldsMap.FromContact.key]);
      setValue(FormFieldsMap.Phones.key, preloadedValues[FormFieldsMap.Phones.key]);

      setUiState(prevState => ({
        ...prevState,
        isLoading: false,
        hasLoaded: true
      }));
    },
    [reset, setValue]
  );

  const fetchSelectedContact = useCallback(
    async contact => {
      if (!contact) return;
      try {
        const isFromContact = contact.role_id === ContactRole.Name;
        setUiState(prevState => ({
          ...prevState,
          isLoading: true,
          hasLoaded: false
        }));

        const baseUrl = isFromContact ? Endpoints.Names : Endpoints.HiringAuthorities;
        const url = `${baseUrl}/${contact.id}`;
        const entityIncludes = isFromContact ? ContactIncludes : HiringAuthorityIncludes;
        const additionalIncludes = [
          canIncludeContactDataSheet && isFromContact && 'dataSheetId'
        ].filter(Boolean);

        const includes = [entityIncludes, ...additionalIncludes].filter(Boolean).join(',');
        const endpoint = queryString.stringifyUrl({
          url,
          query: { includes }
        });

        const response = await API.get(endpoint, { apiVersion: 2 });
        const data = response.data || {};

        const formFieldsMap = isFromContact ? FormFieldsMap : FormFieldsMapForHA;

        const parsedCompanyId = Number(data?.company_id);
        const hasValidCompanyId =
          data?.company_id != null && data?.company_id !== '' && Number.isFinite(parsedCompanyId);
        const companyFromContact =
          isFromContact && hasValidCompanyId && data?.current_company
            ? { id: parsedCompanyId, name: data?.current_company }
            : null;

        const company = preselectedCompany || (isFromContact ? companyFromContact : data?.company);

        const dataSheetPayload = data?.dataSheetId
          ? {
              dataSheet: {
                id: data.dataSheetId,
                progress: {}
              }
            }
          : {};

        preloadContactInfo(
          {
            ...data,
            ...dataSheetPayload,
            company,
            [FormFieldsMap.FromContact.key]: contact
          },
          formFieldsMap
        );
      } catch (error) {
        showAlert({
          title: 'Error',
          error
        });
      } finally {
        setUiState(prevState => ({ ...prevState, isLoading: false }));
      }
    },
    [canIncludeContactDataSheet, preloadContactInfo, preselectedCompany, showAlert]
  );

  useEffect(() => {
    if (!isFromProfile && !resumeHiringId && !resumeContactId) return;

    const loadContactAndSetDraft = async () => {
      if (resumeHiringId || resumeContactId) return;

      await fetchSelectedContact(buildProfileData);
      setDraft(prev => ({ ...prev, isFromDirectory: true }));
    };

    loadContactAndSetDraft();
  }, [
    buildProfileData,
    fetchSelectedContact,
    isFromProfile,
    resumeContactId,
    resumeHiringId,
    setDraft
  ]);
  const toggleDrawer = (anchor, open) => event => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) return;
    setIsBluesheetOpen(open);
  };

  const handleBluesheetClosed = () => {
    isBluesheetOpen && setIsBluesheetOpen(false);

    if (dataSheet) {
      setValue(FormFieldsMap.DataSheet.key, dataSheet);
    }
  };

  const handleFilesChanged = useCallback(
    data => {
      const attachmentsChanged = flatMap(map(data, 'files'));
      setAttachments(attachmentsChanged);
      setDraft(prev => ({ ...prev, attachments: attachmentsChanged }));
    },
    [setDraft]
  );
  const handleCancelClick = () => {
    canBeSavedAsDraft ? saveOnCancel() : history.goBack();
  };

  const onSubmit = async formData => {
    try {
      setUiState(prevState => ({ ...prevState, isSaving: true }));
      const preparedCandidate = prepareFormToSubmit(formData, FormFieldsMap);

      const preparePhoneNumbers = formatPhoneNumbers(formData, PHONE_FORMAT_MODES.submit);
      const candidateData = {
        ...preparedCandidate,
        files: attachments.map(att => att.id),
        phones: preparePhoneNumbers,
        dataSheetId: formData?.dataSheet?.id
      };

      const fromContactId = formData[FormFieldsMap.FromContact.key]?.id;
      const isFromContact = formData[FormFieldsMap.FromContact.key]?.role_id === ContactRole.Name;
      isFromContact && (candidateData.hiringId = null);

      const urlString = isFromContact
        ? `${Endpoints.Candidates}/fromName/:id`.replace(':id', fromContactId)
        : `${Endpoints.Candidates}`;

      const response = await API.post(urlString, candidateData);

      if (response.data && response.status !== HTTPStatusCodes.Created) {
        showAlert({
          severity: 'error',
          title: 'Candidate',
          autoHideDuration: 8000,
          error: response
        });

        setUiState(prevState => ({
          ...prevState,
          isSaving: false
        }));

        return;
      }

      setUiState(prevState => ({
        ...prevState,
        isSuccess: true
      }));

      showAlert({
        severity: 'success',
        title: strings.candidates.creation.success.title,
        body: strings.candidates.creation.success.body
      });

      if (draft?.id) deleteDraftById(draft?.id);

      setTimeout(() => {
        history.push(EntityRoutes.CandidateProfile.replace(':id', response.data.data.id));
      }, waitingTimeBeforeRedirect);
    } catch (error) {
      setUiState(prevState => ({
        ...prevState,
        isSuccess: false
      }));

      showAlert({
        severity: 'error',
        title: 'Candidates',
        autoHideDuration: 5000,
        error
      });
    } finally {
      setUiState(prevState => ({
        ...prevState,
        isSaving: false
      }));
    }
  };
  const handleValueChange = (name, value) => setValue(name, value, true);

  const toggleDirectoryOption = fromDirectory => {
    resetForm({ fromDirectory });
  };

  const handleContactSelect = async (name, value) => {
    if (!value) {
      resetForm({ fromDirectory: true });
      return;
    }
    setValue(name, value, true);
    await fetchSelectedContact(value);
  };

  const isCandidateFormReady =
    !isFromDirectory || (isFromDirectory && selectedContactOrHA?.id && uiState.hasLoaded);

  const resetForm = ({ fromDirectory = false, clearUrl = false }) => {
    setUiState(prev => ({ ...prev, isReseting: true }));
    setIsFromDirectory(fromDirectory);
    setDraft({ ...InitialDraft, type: EntityType.Candidate, isFromDirectory: fromDirectory });
    reset({ [FormFieldsMap.Email.key]: '' });
    setAttachments([]);
    setBluesheet(null);
    clearUrl && history.replace(EntityRoutes.CandidateCreate);
    onResetForm(fromDirectory);
    setTimeout(() => {
      setUiState(prev => ({ ...prev, isReseting: false }));
    }, 500);
  };

  const handleClickReset = () => resetForm({ fromDirectory: false, clearUrl: true });

  const finalHeaderProps = strings.candidates.create.candidateDataSheet.new;

  /** This method is now only updating when the datasheet is completed, or changed from complete to in progress, or when the draft Id is assigned
   * This is to avoid too many re-renders that are making the datasheet form behaves weird on validation
   */
  const handleSaveCandidateDataSheet = useCallback(
    (candidateDataSheetID, progress) => {
      const completed = getValues(FormFieldsMap.BluesheetCompleted.key);
      const wasCompleted = completed === true || completed === 'true';
      if (progress?.answered === progress?.total) {
        setValue(FormFieldsMap.BluesheetCompleted.key, true, true);
        setValue(FormFieldsMap.DataSheet.key, { id: candidateDataSheetID, progress });
      } else if (wasCompleted) setValue(FormFieldsMap.BluesheetCompleted.key, false, true);

      if (!dataSheetId)
        setValue(FormFieldsMap.DataSheet.key, { id: candidateDataSheetID, progress });
    },
    [setValue, dataSheetId, getValues]
  );

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.cancel,
        variant: 'outlined',
        onClick: handleCancelClick,
        size: 'small'
      }
    },
    {
      props: {
        variant: 'outlined',
        disabled: !canBeSavedAsDraft,
        onClick: handleUpdateDraft,
        text: strings.drafts.button.update
      },
      visible: canSaveAsDraft && !!draft?.id
    },
    {
      props: {
        variant: 'outlined',
        disabled: !canBeSavedAsDraft,
        text: strings.drafts.button.save,
        className: classes.saveDraftBtn,
        onClick: () => {
          const currentFormValues = getValues();
          saveDraft({ shouldConfirm: true, values: currentFormValues });
        }
      },
      visible: canSaveAsDraft && !draft?.id
    },
    {
      props: {
        initialText: strings.shared.ui.save,
        variant: 'contained',
        type: 'submit',
        disabled: isSaveDisabled,
        onProgressText: strings.shared.ui.saving,
        isSaving: uiState.isSaving,
        isSuccess: uiState.isSuccess
      },
      mode: 'save'
    }
  ];

  return (
    <>
      <Prompt
        message={(locationPath, action) => {
          if (canBeSavedAsDraft && action === 'PUSH') {
            saveOrUpdate();
          }
          if (locationPath.pathname === EntityRoutes.CandidateCreate && action !== 'POP') {
            resetForm({ fromDirectory: false, clearUrl: false });
          }
        }}
      />

      <React.Fragment key={drawerAnchor}>
        <FormContext {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FPCard className={globalClasses.newItemsSection}>
              <FPCardContent
                variant="relaxed"
                headerProps={{
                  title: strings.candidates.creation.mainSection,
                  subheader: strings.shared.fields.required,
                  classes: { action: classes.actionButtonContainer },
                  variant: 'section',
                  action: (
                    <FPActionButton
                      text={strings.formatString(strings.shared.ui.reset, {
                        item: 'Form'
                      })}
                      className={classes.resetButton}
                      onClick={handleClickReset}
                      variant="text"
                    />
                  )
                }}
              >
                <div className={classes.selectContactContainer}>
                  <div>
                    <When
                      condition={
                        !isFromTalentPool && !isFromResumeParser && !isFromProfile && !draft?.id
                      }
                    >
                      {isFromDirectory ? (
                        <Typography variant="subtitle1">
                          {strings.candidates.creation.selectExistingFromDirectory.message}
                          <FPActionButton
                            text={strings.candidates.creation.selectExistingFromDirectory.link}
                            className={classes.button}
                            onClick={() => toggleDirectoryOption(false)}
                            variant="text"
                          />
                        </Typography>
                      ) : (
                        <Typography variant="body1">
                          {strings.candidates.creation.selectExisting.message}
                          <FPActionButton
                            text={strings.candidates.creation.selectExisting.link}
                            className={classes.button}
                            onClick={() => toggleDirectoryOption(true)}
                            variant="text"
                          />
                          {strings.candidates.creation.selectExisting.continueMessage}
                        </Typography>
                      )}
                    </When>
                  </div>
                </div>

                {emailValidation.severity !== SeverityBySimilarity.None && (
                  <DuplicateCandidateAlert
                    emailValidation={emailValidation}
                    onClickCreateFromContact={handleClickCreateFromContact}
                  />
                )}

                {isFromDirectory && !isFromProfile && (
                  <div className={classes.chooseExistingContact}>
                    <AutocompleteSelect
                      displayKey="full_name"
                      getOptionSelected={Selectors.byId}
                      groupBy={option => option.typeTitle}
                      name={FormFieldsMap.FromContact.key}
                      onSelect={handleContactSelect}
                      placeholder={
                        strings.candidates.creation.selectExistingFromDirectory.placeholder
                      }
                      renderOption={globalSearchPerson('full_name')}
                      selectedValue={selectedContactOrHA}
                      typeahead
                      typeaheadLimit={20}
                      typeaheadParams={{ entityType: 'hiringAuthorityOrName' }}
                      url={Endpoints.Search}
                    />
                    <Divider />
                  </div>
                )}

                {isCandidateFormReady ? (
                  <CandidateForm
                    onValuesChanged={handleValuesChanged}
                    disabled={!isOpsOrDc && emailValidation.isRegistered}
                  />
                ) : (
                  nestTernary(
                    !uiState.hasLoaded,
                    <Grid container justify="center">
                      <CircularProgress size={30} />
                    </Grid>,
                    null
                  )
                )}
              </FPCardContent>
            </FPCard>

            {!emailValidation.isRegistered && (
              <>
                <FPCard className={globalClasses.newItemsSection}>
                  <FPCardContent
                    variant="relaxed"
                    headerProps={{
                      title: finalHeaderProps.title,
                      subheader: finalHeaderProps.subtitle,
                      variant: 'section'
                    }}
                  >
                    {!uiState.isReseting ? (
                      <>
                        <FPActionButton
                          onClick={toggleDrawer(drawerAnchor, true)}
                          text={bluesheet ? 'Edit' : 'Fill In'}
                          disabled={!canBeSavedAsDraft}
                        />
                        {!!form.errors.bluesheet_completed && (
                          <div>
                            <FormControl
                              component="fieldset"
                              error={!!form.errors.bluesheet_completed}
                            >
                              <FormHelperText>
                                {form.errors.bluesheet_completed &&
                                  form.errors.bluesheet_completed.message}
                              </FormHelperText>
                            </FormControl>
                          </div>
                        )}
                      </>
                    ) : (
                      <Grid container justify="center">
                        <CircularProgress size={30} />
                      </Grid>
                    )}
                  </FPCardContent>
                </FPCard>

                <FPCard className={globalClasses.newItemsSection}>
                  <FPCardContent
                    variant="relaxed"
                    headerProps={{
                      title: strings.fileManager.newProfile.sectionTitle,
                      subheader: strings.formatString(
                        strings.fileManager.newProfile.sectionSubtitle,
                        {
                          categorySuggested: 'resume'
                        }
                      ),
                      variant: 'section'
                    }}
                  >
                    {!uiState.isReseting ? (
                      <FileManager
                        apiVersionForDeleting={DELETE_API_VERSION}
                        defaultCategories={DefaultNewCandidateFileCategories}
                        filesEndpoint={Endpoints.Files}
                        initialFiles={candidateFiles}
                        isNewEntity
                        module={CandidateEntity.module}
                        onAttachmentsChanged={handleFilesChanged}
                        showItemDate
                        showItemMenu
                        viewItemOnClick
                      />
                    ) : (
                      <Grid container justify="center">
                        <CircularProgress size={30} />
                      </Grid>
                    )}
                  </FPCardContent>
                </FPCard>
              </>
            )}

            {isUserCoach && (
              <FPCard className={globalClasses.newItemsSection}>
                <FPCardContent
                  variant="relaxed"
                  headerProps={{
                    title: strings.candidates.creation.assignRecruiter.title,
                    subheader: strings.candidates.creation.assignRecruiter.subtitle,
                    variant: 'section'
                  }}
                >
                  <div className="inputContainer">
                    <AutocompleteSelect
                      name={FormFieldsMap.Recruiter.key}
                      selectedValue={selectedRecruiter}
                      placeholder={strings.candidates.creation.assignRecruiter.placeholder}
                      displayKey="full_name"
                      url={`${Endpoints.Recruiters}/myTeam`}
                      onSelect={handleValueChange}
                    />
                  </div>
                </FPCardContent>
              </FPCard>
            )}
            <div className={globalClasses.newItemsActions}>
              <FPButtonBuilder
                buttonDefinitions={buttonDefinitions}
                GridContainerProps={{ spacing: 2, justify: 'flex-end' }}
              />
            </div>
          </form>
          {(uiState.isLoading || emailValidation.isValidating) && (
            <Grid container justify="flex-end" data-testid="loading-and-checking">
              <CircularProgress size={30} />
            </Grid>
          )}
        </FormContext>
        <When condition={isBluesheetOpen}>
          <CandidateDataSheetDrawer
            drawerTitle={finalHeaderProps.title}
            onClose={handleBluesheetClosed}
            onSaveCandidateDataSheet={handleSaveCandidateDataSheet}
            candidateDataSheetId={dataSheet?.id}
          />
        </When>
      </React.Fragment>
    </>
  );
};

export default NewCandidateContent;
